(() => {
  if (customElements.get('sticky-add-to-cart')) return;
  customElements.define('sticky-add-to-cart', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = document.getElementById(this.dataset.formId);
      this.mainButton = this.form?.querySelector('[name="add"]');
      this.mainQuantity = this.form?.querySelector('[name="quantity"]');
      if (!this.mainButton || !this.mainQuantity) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.bar = this.querySelector('.sticky-purchase');
      this.quantity = this.querySelector('[data-quantity]');
      this.add = this.querySelector('[data-add]');
      this.error = this.querySelector('[data-error-message]');
      this.mainError = document.createElement('p');
      this.mainError.className = 'product-purchase-error';
      this.mainError.setAttribute('role', 'alert');
      this.mainError.hidden = true;
      this.mainButton.after(this.mainError);
      // Reserve space at the end of the page, not alongside the observed button.
      this.spacer = document.createElement('div');
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.spacer);
      this.querySelector('[data-minus]').addEventListener('click', () => this.changeQuantity(-1), options);
      this.querySelector('[data-plus]').addEventListener('click', () => this.changeQuantity(1), options);
      this.quantity.addEventListener('change', () => this.commitQuantity(), options);
      this.add.addEventListener('click', () => {
        this.commitQuantity();
        this.mainButton.click();
      }, options);
      // Capture the existing button before its Alpine click handler to share one request.
      this.form.addEventListener('click', (event) => {
        if (!event.target.closest('[name="add"]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit();
      }, { ...options, capture: true });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit();
      }, { ...options, capture: true });
      this.form.addEventListener('input', () => this.sync(), options);
      this.form.addEventListener('change', () => this.sync(), options);
      this.form.addEventListener('quantity-changed', () => queueMicrotask(() => this.sync()), options);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.mainButton, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
      this.observer.observe(this.mainQuantity, { attributes: true, attributeFilter: ['min', 'max', 'step'] });
      this.intersection = new IntersectionObserver(() => this.visibility());
      this.intersection.observe(this.mainButton);
      this.resize = new ResizeObserver(() => this.visibility());
      this.resize.observe(this.bar);
      this.resize.observe(this.mainButton);
      window.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.visibility(); });
      }, { ...options, passive: true });
      window.addEventListener('resize', () => this.visibility(), options);
      window.addEventListener('pageshow', () => this.visibility(), options);
      this.sync();
      this.visibility();
    }
    sync() {
      if (!this.busy) this.available = !this.mainButton.disabled;
      this.add.disabled = this.busy || !this.available;
      this.add.textContent = this.busy ? this.dataset.loading : this.mainButton.textContent.trim();
      this.add.setAttribute('aria-busy', String(Boolean(this.busy)));
      this.quantity.value = this.mainQuantity.value;
      for (const attr of ['min', 'max', 'step']) {
        if (this.mainQuantity.hasAttribute(attr)) this.quantity.setAttribute(attr, this.mainQuantity.getAttribute(attr));
        else this.quantity.removeAttribute(attr);
      }
      this.querySelector('[data-minus]').disabled = this.busy || Number(this.quantity.value) <= Number(this.quantity.min || 1);
      this.querySelector('[data-plus]').disabled = this.busy || Boolean(this.quantity.max && Number(this.quantity.value) >= Number(this.quantity.max));
      this.quantity.disabled = Boolean(this.busy);
    }
    changeQuantity(direction) {
      direction > 0 ? this.quantity.stepUp() : this.quantity.stepDown();
      this.commitQuantity();
    }
    commitQuantity() {
      const min = Number(this.quantity.min || 1);
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      const step = Number(this.quantity.step || 1);
      const value = Number(this.quantity.value);
      const valid = Math.min(max, Math.max(min, min + Math.floor(((Number.isFinite(value) ? value : min) - min) / step) * step));
      this.mainQuantity.value = String(valid);
      this.mainQuantity.dispatchEvent(new Event('input', { bubbles: true }));
      this.mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }
    visibility() {
      const rect = this.mainButton.getBoundingClientRect();
      const visible = rect.height > 0 && rect.bottom <= 0;
      this.bar.hidden = !visible;
      this.spacer.style.height = visible ? this.bar.getBoundingClientRect().height + 'px' : '0px';
    }
    async submit() {
      if (this.busy || this.mainButton.disabled) return;
      this.error.hidden = this.mainError.hidden = true;
      if (!this.mainQuantity.reportValidity()) return;
      this.available = !this.mainButton.disabled;
      this.busy = true;
      this.mainButton.setAttribute('aria-busy', 'true');
      this.sync();
      try {
        const response = await fetch(this.dataset.cartUrl + '.js', {
          method: 'POST', body: new FormData(this.form),
          headers: { Accept: 'application/json' }
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        window.dispatchEvent(new CustomEvent('cart-updated'));
        window.dispatchEvent(new CustomEvent('toggle-cart'));
      } catch (error) {
        const message = error instanceof TypeError ? this.dataset.error : error.message || this.dataset.error;
        this.error.textContent = this.mainError.textContent = message;
        this.error.hidden = this.mainError.hidden = false;
      } finally {
        this.busy = false;
        this.mainButton.removeAttribute('aria-busy');
        this.sync();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.spacer?.remove();
      this.mainError?.remove();
      this.controller = null;
    }
  });
})();

(() => {
  const header = document.querySelector('[data-sticky-header]');

  if (!header) return;

  const setHeaderHeight = () => {
    const headerHeight = header.getBoundingClientRect().height;

    document.documentElement.style.setProperty(
      '--header-height',
      `${headerHeight}px`
    );
  };

  setHeaderHeight();

  window.addEventListener('resize', setHeaderHeight);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(setHeaderHeight);
    observer.observe(header);
  }
})();